import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { booleanSchema, numberSchema, stringSchema } from 'src/Configuration/validation/rules'
import { boolean, object, ObjectSchema } from 'yup'
import { EdisonWallBoxFormData } from '../../models/edisonWallBox'

/**
 * This hook provides the validation schema for the edison step
 */
const MAX_STRING_INPUT_LENGTH = 255
const MAX_BATTERY_INPUT_VALUE = 999

const useEdisonValidation = (): {
  inProgressValidationSchema: ObjectSchema<EdisonWallBoxFormData>
  finalValidationSchema: ObjectSchema<EdisonWallBoxFormData>
} => {
  const { t } = useCustomTranslation()
  const stringSchemaWithTranslation = stringSchema(t)
  const numberSchemaWithTranslation = numberSchema(t)
  const booleanSchemaWithTranslation = booleanSchema(t)

  const inProgressValidationSchema = object().shape({
    serialNumbers: object().shape({
      connect: stringSchemaWithTranslation({ maxLength: MAX_STRING_INPUT_LENGTH }),
      power: stringSchemaWithTranslation({ maxLength: MAX_STRING_INPUT_LENGTH }),
    }),
    vehicleInformation: object().shape({
      manufacturer: stringSchemaWithTranslation({ maxLength: MAX_STRING_INPUT_LENGTH }),
      model: stringSchemaWithTranslation({ maxLength: MAX_STRING_INPUT_LENGTH }),
      batteryCapacity: numberSchemaWithTranslation({ max: MAX_BATTERY_INPUT_VALUE }),
      softwareVersion: stringSchemaWithTranslation({ maxLength: MAX_STRING_INPUT_LENGTH }),
    }),
    functionalityTests: object().shape({
      fullyInstalled: boolean().required(),
      configured: boolean().required(),
      scenarioOneTested: boolean().required(),
      scenarioTwoTested: boolean().required(),
      customerBriefed: boolean().required(),
      biDirectionalDCChargingConfiguration: object().shape({
        positionedAtChargingPoint: boolean().required(),
        chargingLocationDefined: boolean().required(),
        functionActivated: boolean().required(),
      }),
    }),
  })

  const finalValidationSchema: ObjectSchema<EdisonWallBoxFormData> = object().shape({
    serialNumbers: object().shape({
      connect: stringSchemaWithTranslation({ maxLength: MAX_STRING_INPUT_LENGTH }).required(),
      power: stringSchemaWithTranslation({ maxLength: MAX_STRING_INPUT_LENGTH }).required(),
    }),
    vehicleInformation: object().shape({
      manufacturer: stringSchemaWithTranslation({ maxLength: MAX_STRING_INPUT_LENGTH }).required(),
      model: stringSchemaWithTranslation({ maxLength: MAX_STRING_INPUT_LENGTH }).required(),
      batteryCapacity: numberSchemaWithTranslation({ max: MAX_BATTERY_INPUT_VALUE }).required(),
      softwareVersion: stringSchemaWithTranslation({ maxLength: MAX_STRING_INPUT_LENGTH }).required(),
    }),
    functionalityTests: object().shape({
      fullyInstalled: booleanSchemaWithTranslation({ hasToBeTrue: true }).required(),
      configured: booleanSchemaWithTranslation({ hasToBeTrue: true }).required(),
      scenarioOneTested: booleanSchemaWithTranslation({ hasToBeTrue: true }).required(),
      scenarioTwoTested: boolean().required(),
      customerBriefed: booleanSchemaWithTranslation({ hasToBeTrue: true }).required(),
      biDirectionalDCChargingConfiguration: object().shape({
        positionedAtChargingPoint: boolean().required(),
        chargingLocationDefined: boolean().required(),
        functionActivated: boolean().required(),
      }),
    }),
  })

  return { inProgressValidationSchema, finalValidationSchema }
}

export default useEdisonValidation
